import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { DollarSign, TrendingUp, Brain, Calendar, Download } from "lucide-react";
import { useOutletContext } from "react-router-dom";
import { fetchExternalLists, getPaymentReport, getSubscriedUsers } from "./helper";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PDFDocument } from 'pdf-lib';

const Overall = () => {
  const context = useOutletContext();
  const now = new Date();

  const [currency, setCurrency] = useState("INR");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [activeTab, setActiveTab] = useState("ONETIME");
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showExclusiveGST, setShowExclusiveGST] = useState(false);
  const [limit] = useState(10);
  const [combinedDownloadLoading, setCombinedDownloadLoading] = useState(false);

  const formatAmount = (amount) => {
    if (!amount) return "0";
    return currency === "INR"
      ? amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })
      : amount.toLocaleString("en-US", { maximumFractionDigits: 2 });
  };
  const handleDownload = async (downloadUrl, invoiceId) => {
    if (!downloadUrl) {
      alert("Download URL not available");
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Invoice_${invoiceId}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading invoice:", error);
      alert("Failed to download invoice");
    }
  };

  const handleCombinedDownload = async () => {
    setCombinedDownloadLoading(true);
    try {
      const allUrls = [];
      for (let page = 1; page <= totalPages; page++) {
        let res;
        if (activeTab === "EXTERNAL") {
          res = await fetchExternalLists(
            page - 1,
            limit,
            'tax',
            '',
            '',
            startDate || null,
            endDate || null,
            currency,
            startDate ? null : month,
            startDate ? null : year
          );
        } else if (activeTab === "SUBSCRIPTION") {
          res = await getSubscriedUsers(
            page,
            limit,
            "SUBSCRIPTION",
            '',
            currency,
            startDate || null,
            endDate || null,
            startDate ? null : month,
            startDate ? null : year
          );
        } else if (activeTab === "ONETIME") {
          res = await getSubscriedUsers(
            page,
            limit,
            "ONE_TIME",
            '',
            currency,
            startDate || null,
            endDate || null,
            startDate ? null : month,
            startDate ? null : year
          );
        }
        if (res?.data) {
          const data = activeTab === "EXTERNAL" ? res.data.data.result : res.data.result;
          if (data && data.length > 0) {
            if (activeTab === 'EXTERNAL') {
              data.forEach(invoice => {
                if (invoice.url) allUrls.push(invoice.url);
              });
            } else {
              data.forEach(user => {
                if (user.gst_invoice_url) allUrls.push(user.gst_invoice_url);
              });
            }
          }
        }
      }
      if (allUrls.length === 0) {
        alert("No PDFs available for download");
        return;
      }
      const pdfDoc = await PDFDocument.create();
      for (const url of allUrls) {
        try {
          const response = await fetch(url);
          const arrayBuffer = await response.arrayBuffer();
          const pdf = await PDFDocument.load(arrayBuffer);
          const copiedPages = await pdfDoc.copyPages(pdf, pdf.getPageIndices());
          copiedPages.forEach(page => pdfDoc.addPage(page));
        } catch (error) {
          console.error("Error fetching PDF:", url, error);
        }
      }
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${activeTab}_Combined_Invoices.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error creating combined PDF:", error);
      alert("Failed to create combined PDF");
    } finally {
      setCombinedDownloadLoading(false);
    }
  };
  useEffect(() => {
    context(["Payments & Invoice", "Overall"]);
  }, [context]);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const res = await getPaymentReport(
          startDate ? null : month, 
          startDate ? null : year, 
          currency, 
          startDate, 
          endDate
        );
        if (res?.data) setReportData(res.data);
      } catch (error) {
        console.error("Error fetching payment report:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [month, year, currency, startDate, endDate]);

  useEffect(() => {
    fetchTableData();
  }, [activeTab, currentPage, currency, startDate, endDate, month, year]);

  const fetchTableData = async () => {
    try {
      setTableLoading(true);
      let res;

      if (activeTab === "EXTERNAL") {
        res = await fetchExternalLists(
          currentPage - 1,
          limit,
          'tax',
          '',
          '',
          startDate || null,
          endDate || null,
          currency,
          startDate ? null : month,
          startDate ? null : year
        );
        if (res?.data) {
          setTableData(res.data.data.result || []);
          setTotalPages(res.data.data.meta.pages || 1);
        }
      } else if (activeTab === "SUBSCRIPTION") {
        res = await getSubscriedUsers(
          currentPage,
          limit,
          "SUBSCRIPTION",
          '',
          currency,
          startDate || null,
          endDate || null,
          startDate ? null : month,
          startDate ? null : year
        );
        if (res?.data) {
          setTableData(res.data.result || []);
          setTotalPages(res.data.meta.pages || 1);
        }
      } else if (activeTab === "ONETIME") {
        res = await getSubscriedUsers(
          currentPage,
          limit,
          "ONE_TIME",
          '',
          currency,
          startDate || null,
          endDate || null,
          startDate ? null : month,
          startDate ? null : year
        );
        if (res?.data) {
          setTableData(res.data.result || []);
          setTotalPages(res.data.meta.pages || 0);
        }
      }
    } catch (error) {
      console.error("Error fetching table data:", error);
      setTableData([]);
    } finally {
      setTableLoading(false);
    }
  };

  const currencySymbol = currency === "INR" ? "₹" : "$";

  const currentMonth = now.getMonth() + 1;
  const averageMonthlyRevenue = reportData?.totalRevenueWithGst ? parseFloat(reportData.totalRevenueWithGst) / currentMonth : 0;
  const chartData = [];
  if (reportData?.ONETIME_SUBSCRIPTION) {
    chartData.push({
      name: "One-time Subscription",
      value: parseFloat(reportData.ONETIME_SUBSCRIPTION.net_amount),
      count: parseInt(reportData.ONETIME_SUBSCRIPTION.total_count),
      color: "#4F46E5",
    });
  }
  if (reportData?.SUBSCRIPTION?.monthly) {
    chartData.push({
      name: "Monthly Subscription",
      value: parseFloat(reportData.SUBSCRIPTION.monthly.net_amount),
      count: parseInt(reportData.SUBSCRIPTION.monthly.total_count),
      color: "#10B981",
    });
  }
  if (reportData?.SUBSCRIPTION?.yearly) {
    chartData.push({
      name: "Yearly Subscription",
      value: parseFloat(reportData.SUBSCRIPTION.yearly.net_amount),
      count: parseInt(reportData.SUBSCRIPTION.yearly.total_count),
      color: "#F59E0B",
    });
  }

  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" },
  ];

  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-800">{data.name}</p>
          <p className="text-sm text-gray-600">Revenue: {currencySymbol}{data.value}</p>
          <p className="text-sm text-gray-600">Count: {data.count}</p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => (
    <div className="flex flex-wrap justify-center gap-4 mt-6">
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-sm text-gray-700 font-medium">{entry.value}</span>
        </div>
      ))}
    </div>
  );

  const handleClearAll = () => {
    setStartDate("");
    setEndDate("");
    setMonth(now.getMonth() + 1);
    setYear(now.getFullYear());
    setCurrency("INR");
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderExternalRows = () => {
    if (tableLoading) {
      return (
        <tr>
          <td colSpan="10" className="px-6 py-4 text-center text-gray-500">Loading...</td>
        </tr>
      );
    }

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td colSpan="10" className="px-6 py-4 text-center text-gray-500">No data available</td>
        </tr>
      );
    }

    return tableData.map((invoice, index) => (
      <tr className="bg-white border-b text-sm font-light" key={invoice.id || index}>
        <td className="py-4 px-6">{(currentPage - 1) * limit + index + 1}</td>
        <td className="py-4 px-6">{invoice.invoiceNumber || invoice.id || 'N/A'}</td>
        <td className="py-4 px-6">{invoice.companyName || 'N/A'}</td>
        <td className="py-4 px-6">{invoice.companyEmail || 'N/A'}</td>
        <td className="py-4 px-2">
          {invoice.created_at
            ? new Date(invoice.created_at).toLocaleString("en-GB", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }).replace(",", "")
              .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$2-$1")
              .replace(" ", ", ")
            : ""}
        </td>
        <td className="py-4 px-6">{invoice?.currency === 'USD' ? '$' : '₹'}{invoice.amount || 0}</td>
        <td className="py-4 px-6">{invoice?.currency === 'USD' ? '$' : '₹'}{invoice.gst || 0}</td>
        <td className="py-4 px-6">{invoice?.currency === 'USD' ? '$' : '₹'}{invoice.total || 0}</td>
        <td className="py-4 px-6">{invoice.description || 'N/A'}</td>
        <td className="py-4 px-">
          {invoice.url && (
            <button
              onClick={() => handleDownload(
                invoice.url,
                invoice.invoiceNumber || invoice.id
              )}
              className="bg-[#2EBAA3] text-white px-3 py-1 rounded text-xs font-medium transition-colors flex items-center"
              title="Download Invoice"
            >
              Download
            </button>
          )}
        </td>
      </tr>
    ))
  };

  const renderSubscriptionRows = () => {
    if (tableLoading) {
      return (
        <tr>
          <td colSpan="9" className="px-6 py-4 text-center text-gray-500">Loading...</td>
        </tr>
      );
    }

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td colSpan="9" className="py-4 text-center text-gray-500">No data available</td>
        </tr>
      );
    }

    return tableData.map((user, index) => (
      <tr className="bg-white border-b text-sm font-light" key={user.id || index}>
        <th
          scope="row"
          className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap cursor-pointer"
          key={index}
        >
         {(currentPage - 1) * limit + index + 1}
        </th>
        <td className="py-4 px-6">{user.platform_invoice_number || ' '}</td>

        <td className="py-4 px-6 cursor-pointer hover:underline">
          {user?.user ? user.user.company : ""}
        </td>
        <td className="py-4 px-6">
          {user?.user ? user.user.email : ""}
        </td>
        <td className="py-4 px-6">
          {user?.user ? user.user.username : ""}
        </td>
        <td className="py-4 px-6">
          {user.gst_invoice_url && (
            <button
              onClick={() => handleDownload(
                user.gst_invoice_url,
                user.gst_invoice_url
              )}
              className="bg-[#2EBAA3] text-white px-3 py-1 rounded text-xs font-medium transition-colors flex items-center"
              title="Download Invoice"
            >
              Download
            </button>
          )}
        </td>
        <td className="py-4 px-6 ">
          {user?.payment_for ? user.payment_for : user.interval}
        </td>
        {activeTab === 'ONETIME' ? <td className="py-4 px-6 ">
          {user.payment_provider?.toLowerCase() === 'stripe' ? '$' : '₹'}{user?.amount}
        </td> : <td className="py-4 px-6 ">
          {user?.attributes?.id}
        </td>}
        <td className="py-4 px-6">
          {user?.created_at
            ? new Date(user.created_at).toLocaleString("en-GB", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }).replace(",", "")
              .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$2-$1")
              .replace(" ", ", ")
            : ""}
        </td>
      </tr>
    ));
  };


  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-end px-6 py-3 bg-white border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <p className="text-center text-gray-500 py-10">Loading report...</p>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header & Filters */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-3">
          <h1 className="text-2xl font-bold text-[#2EBAA3]">Overall Data</h1>
          <div className="min-w-fit flex flex-row gap-2">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg shadow-sm text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="INR">INR (₹)</option>
                  <option value="USD">USD ($)</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">Start Date</label>
                <DatePicker
                  selected={startDate ? new Date(startDate) : null}
                  onChange={(date) => {
                    setStartDate(date ? date.toISOString().split("T")[0] : "");
                    setCurrentPage(1); // Reset pagination when date changes
                  }}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="dd/mm/yyyy"
                  maxDate={new Date()}
                  className="px-2 py-1 border border-gray-300 rounded-lg text-sm w-32"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">End Date</label>
                <DatePicker
                  selected={endDate ? new Date(endDate) : null}
                  onChange={(date) => {
                    setEndDate(date ? date.toISOString().split("T")[0] : "");
                    setCurrentPage(1); // Reset pagination when date changes
                  }}
                  dateFormat="dd-MM-yyyy"
                  minDate={startDate ? new Date(startDate) : null}
                  maxDate={new Date()}
                  placeholderText="dd/mm/yyyy"
                  className="px-2 py-1 border border-gray-300 rounded-lg text-sm w-32"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">Month</label>
                <select
                  value={month}
                  onChange={(e) => {
                    setMonth(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  disabled={startDate && endDate} // Disable when date range is selected
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg shadow-sm text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-medium text-gray-700">Year</label>
                <select
                  value={year}
                  onChange={(e) => {
                    setYear(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  disabled={startDate && endDate} // Disable when date range is selected
                  className="w-full px-3 py-1 border border-gray-300 rounded-lg shadow-sm text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-end mb-1">
              <button
                onClick={handleClearAll}
                className="px-3 py-1 text-sm bg-[#2EBAA3] text-white rounded-lg shadow-md hover:bg-[#26a691] transition-colors duration-200"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>

        {/* Revenue Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium mb-1">
                  {showExclusiveGST ? "Exclusive GST" : "Total Revenue(Inclusive GST)"}
                </p>
                <p className="text-3xl font-bold text-gray-900">
                  {currencySymbol}
                  {showExclusiveGST
                    ? formatAmount(parseFloat(reportData?.totalRevenue || 0))
                    : formatAmount(parseFloat(reportData?.totalRevenueWithGst || 0))}
                </p>
              </div>
              <div className="flex flex-col items-end">
                <button
                  onClick={() => setShowExclusiveGST(!showExclusiveGST)}
                  className={`text-xs px-3 py-1 mb-2 rounded-md border shadow-sm transition-colors duration-200
                    ${showExclusiveGST
                      ? "bg-[#2EBAA3] text-white border-[#2EBAA3]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                    }`}
                >
                  {showExclusiveGST ? "Inclusive GST" : "Exclusive GST"}
                </button>
                <div className="bg-gradient-to-br from-green-100 to-green-200 w-10 h-10 rounded-full flex items-center justify-center">
                  {currency === "INR" ? (
                    <span className="text-green-600 font-bold text-lg">₹</span>
                  ) : (
                    <DollarSign className="text-green-600" size={20} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium mb-1">Total Purchases</p>
                <p className="text-3xl font-bold text-gray-900">{reportData?.totalPurchases || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-2 rounded-full">
                <TrendingUp className="text-blue-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mb-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Revenue Distribution</h2>
            <p className="text-gray-600">Billing breakdown by service type</p>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={160}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[400px] flex items-center justify-center">
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-700">📊 No Graph Data Found</p>
                <p className="text-sm text-gray-500">Try selecting a different date range</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs and Table */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 mb-8">
          <div className="border-b flex flex-row justify-between border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { key: 'ONETIME', label: 'One Time Payments' },
                { key: 'SUBSCRIPTION', label: 'Platform Subscription' },
                { key: 'EXTERNAL', label: 'External Payments' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === tab.key
                    ? 'border-[#2EBAA3] text-[#2EBAA3]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.label}
                </button>
              ))}  
            </nav>
            {(activeTab === 'ONETIME' || activeTab === 'EXTERNAL' || activeTab === 'SUBSCRIPTION') && (
                <button
                  onClick={handleCombinedDownload}
                  disabled={combinedDownloadLoading}
                  className="h-[40px] mt-2 mr-3 py-2 px-3 bg-[#2EBAA3] text-white text-sm rounded-lg shadow-md hover:bg-[#26a691] transition-colors duration-200 disabled:opacity-50 flex items-center gap-2"
                >
                  <Download size={14} />
                  {combinedDownloadLoading ? 'Downloading...' : 'Download All Invoices'}
                </button>
              )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {activeTab === "EXTERNAL" ?
                  <tr className="text-sm font-normal text-gray-600">
                    <th className="py-3 px-6 text-left">S. No.</th>
                    <th className="py-3 px-6 text-left">Invoice No.</th>
                    <th className="py-3 px-6 text-left">Company Name</th>
                    <th className="py-3 px-6 text-left">Company Email</th>
                    <th className="py-3 px-6 text-left">Created On</th>
                    <th className="py-3 px-6 text-left">Amount</th>
                    <th className="py-3 px-6 text-left">GST</th>
                    <th className="py-3 px-6 text-left">Total Amount</th>
                    <th className="py-3 px-6 text-left">Content</th>
                    <th className="py-3 px-6 text-left">Actions</th>
                  </tr>
                  : <tr className="text-sm font-normal text-gray-600">
                    <th scope="col" className="py-3 px-6 text-left">Sr.</th>
                    <th scope="col" className="py-3 px-6 text-left">Invoice No.</th>
                    <th scope="col" className="py-3 px-6 text-left">Company Name</th>
                    <th scope="col" className="py-3 px-6 text-left">Email</th>
                    <th scope="col" className="py-3 px-6 text-left">Username</th>
                    <th scope="col" className="py-3 px-6 text-left">Invoice</th>
                    <th scope="col" className="py-3 px-6 text-left">{activeTab === 'ONETIME' ? 'Payment for' : 'Interval'}</th>
                    {activeTab === 'ONETIME' ? <th scope="col" className="py-3 px-6 text-left">Amount</th> : <th scope="col" className="py-3 px-6 text-left uppercase">Plan Type</th>}
                    <th scope="col" className="py-3 px-6 text-left">Purchase at</th>
                  </tr>}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activeTab === "EXTERNAL" ? renderExternalRows() : renderSubscriptionRows()}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default Overall;